const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'canceled',
]);

// A timed-out DELETE can have reached the server even when the browser never
// received its run id. Reconcile for roughly 30 seconds before allowing a new,
// explicitly confirmed request. The backend identity makes that retry
// idempotent if the first admission appears late.
export const UNKNOWN_ADMISSION_MAX_NOT_FOUND_POLLS = 10;

export const normalizeInboxDeletionStatus = (status) =>
  String(status || 'unknown')
    .trim()
    .toLowerCase();

export const normalizeInboxDeletionRun = (payload, inboxId = null) => {
  const source = payload?.payload || payload?.deletion_run || payload || {};
  return {
    id: source.deletion_run_id ?? source.id ?? null,
    inboxId: Number(source.inbox_id ?? inboxId) || null,
    status: normalizeInboxDeletionStatus(
      source.deletion_status ?? source.status
    ),
    currentStep: source.current_step ?? source.currentStep ?? null,
    errorCode: source.error_code ?? source.errorCode ?? null,
    updatedAt: source.updated_at ?? source.updatedAt ?? null,
    steps: source.steps || {},
    attempts: Number(source.attempts) || 0,
    completedAt: source.completed_at ?? source.completedAt ?? null,
    lastError: source.last_error ?? source.lastError ?? null,
    outcomeUnknown: false,
    notFoundPolls: 0,
    retryAllowed: false,
  };
};

export const isInboxDeletionTerminal = (status) =>
  TERMINAL_STATUSES.has(normalizeInboxDeletionStatus(status));

export const isInboxDeletionCompleted = (status) =>
  normalizeInboxDeletionStatus(status) === 'completed';

export const isInboxDeletionNetworkOutcomeUnknown = (error) => {
  const response = error?.response;
  const status = Number(response?.status);
  const data = response?.data;
  const hasStructuredServerOutcome =
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    [data.error, data.message, data.error_code].some(
      (value) => typeof value === 'string' && value.trim().length > 0
    );

  if (hasStructuredServerOutcome) return false;

  return (
    !response ||
    error?.code === 'ECONNABORTED' ||
    status === 408 ||
    status === 502 ||
    status === 504 ||
    /timeout|network error/i.test(String(error?.message || ''))
  );
};

export const canRetryInboxDeletion = (run) => Boolean(run?.retryAllowed);

export const reconcileMissingInboxDeletionRun = (run) => {
  const notFoundPolls = (Number(run?.notFoundPolls) || 0) + 1;
  const retryAllowed =
    !run?.id && notFoundPolls >= UNKNOWN_ADMISSION_MAX_NOT_FOUND_POLLS;

  return {
    ...run,
    status: retryAllowed ? 'not_started' : run?.status || 'unknown',
    outcomeUnknown: !retryAllowed,
    notFoundPolls,
    retryAllowed,
  };
};
